import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Modal, Card, DatePicker, Select } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DownloadOutlined, DeleteOutlined, SearchOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import authService from '../utils/auth';
import { generateInvoiceHtml, openInvoicePrintWindow } from '../utils/invoiceTemplate';
import InvoiceFormModal from './InvoiceCreate';

const { RangePicker } = DatePicker;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

export default function InvoiceRecords() {
  const userInfo = authService.getUserInfo();
  const role = userInfo?.role;
  const isStaff = role === 'admin' || role === 'finance';

  const [createOpen, setCreateOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const [dateRange, setDateRange] = useState(null);
  const [salespersonId, setSalespersonId] = useState(undefined);
  const [salespersonOptions, setSalespersonOptions] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, pageSize };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      if (isStaff && salespersonId) {
        params.salespersonId = salespersonId;
      }
      const res = await axios.get(`${API_BASE}/invoices`, {
        headers: authService.getAuthHeaders(),
        params,
      });
      setData(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      message.error(err.response?.data?.error || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize, dateRange, salespersonId, isStaff]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!isStaff) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/invoices/salespersons`, {
          headers: authService.getAuthHeaders(),
        });
        setSalespersonOptions((res.data.data || []).map((s) => ({ value: s.id, label: s.name })));
      } catch (err) {
        // ignore
      }
    })();
  }, [isStaff]);

  const handleView = (record) => {
    setPreviewHtml(generateInvoiceHtml(record));
    setPreviewOpen(true);
  };

  const handleEdit = (record) => {
    setEditingInvoice(record);
    setCreateOpen(true);
  };

  const handleCloseModal = () => {
    setCreateOpen(false);
    setEditingInvoice(null);
  };

  const handleDownload = async (record) => {
    try {
      const res = await axios.get(`${API_BASE}/invoices/${record.id}/download-pdf`, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${record.invoiceNo || record.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('PDF 下载失败');
    }
  };

  const handleSendEmail = (record) => {
    let to = record.billToEmail || '';
    Modal.confirm({
      title: `发送 Invoice ${record.invoiceNo}`,
      content: (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 6, fontSize: 13, color: '#666' }}>收件邮箱：</div>
          <Input defaultValue={to} onChange={(e) => { to = e.target.value; }} placeholder="recipient@example.com" />
        </div>
      ),
      okText: '发送',
      cancelText: '取消',
      onOk: async () => {
        if (!to || !to.trim()) { message.warning('请输入收件邮箱'); return Promise.reject(); }
        try {
          await axios.post(`${API_BASE}/invoices/${record.id}/send-email`, { to: to.trim() }, { headers: authService.getAuthHeaders() });
          message.success('邮件已发送');
        } catch (err) {
          message.error(err.response?.data?.error || '发送失败');
          throw err;
        }
      },
    });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/invoices/${id}`, { headers: authService.getAuthHeaders() });
      message.success('已删除');
      fetchList();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const columns = [
    { title: 'Invoice No.', dataIndex: 'invoiceNo', key: 'invoiceNo' },
    {
      title: 'Date', dataIndex: 'invoiceDate', key: 'invoiceDate',
      render: (v) => v ? new Date(v).toLocaleDateString('en-NZ') : '-'
    },
    { title: 'Bill To', dataIndex: 'billToName', key: 'billToName' },
    {
      title: 'Total', dataIndex: 'total', key: 'total',
      render: (v) => `NZD ${Number(v || 0).toFixed(2)}`,
      align: 'right',
    },
    ...(isStaff ? [{
      title: 'Created By', dataIndex: 'creator', key: 'creator',
      render: (creator, record) => creator?.name || record.salesRepName || '-',
    }] : []),
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt',
      render: (v) => v ? new Date(v).toLocaleDateString('en-NZ') : '-',
    },
    {
      title: 'Actions', key: 'actions', width: 320,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>PDF</Button>
          <Button size="small" type="primary" icon={<MailOutlined />} onClick={() => handleSendEmail(record)}>Email</Button>
          <Popconfirm title="Delete this invoice?" onConfirm={() => handleDelete(record.id)} okText="Delete" cancelText="Cancel">
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        bordered={false}
        title="Invoice History"
        extra={
          <Space>
            <Input
              placeholder="搜索 Invoice No. / Bill To"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 240 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingInvoice(null); setCreateOpen(true); }}>
              New Invoice
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 12 }} wrap>
          <RangePicker
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
            placeholder={['开始日期', '结束日期']}
            format="YYYY-MM-DD"
          />
          {isStaff && (
            <Select
              allowClear
              placeholder="按销售筛选"
              style={{ width: 200 }}
              value={salespersonId}
              onChange={(v) => { setSalespersonId(v); setPage(1); }}
              options={salespersonOptions}
            />
          )}
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <InvoiceFormModal
        open={createOpen}
        onClose={handleCloseModal}
        onSaved={fetchList}
        invoice={editingInvoice}
      />

      <Modal
        title="Invoice 预览"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={780}
        maskClosable={false}
        keyboard={false}
        footer={<Button onClick={() => setPreviewOpen(false)}>关闭</Button>}
      >
        <iframe
          title="invoice-preview"
          srcDoc={previewHtml}
          style={{ width: '100%', height: '70vh', border: '1px solid #eee' }}
        />
      </Modal>
    </div>
  );
}
